// src/pages/services/factory-compliance.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Factory, HardHat, ClipboardList, ShieldCheck, AlertTriangle } from 'lucide-react';

const FactoryCompliance = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 800], [0, -80]); 

  const handleFreeConsultation = () => navigate('/contact');

  const scopeItems = [
    "Factory registration and plan approval coordination",
    "Annual licence renewals under the Factories Act",
    "Safety and welfare statutory register maintenance",
    "Periodic returns and reports filing",
    "Health, Safety & Environment (HSE) compliance management",
    "Liaison with factory inspectorates",
    "Audit preparation and mock inspection support",
    "Post-inspection closure and corrective documentation",
    "Hazardous process compliance (where applicable)"
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">
      {/* Hero Section – luxurious & animated */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50 pt-24 overflow-hidden">
        {/* Floating orbs */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], rotate: [0, 4, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-[900px] h-[900px] bg-emerald-200/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, -5, 0] }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-40 -right-40 w-[1000px] h-[1000px] bg-cyan-200/25 rounded-full blur-3xl"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-4 bg-white/80 backdrop-blur-xl border border-emerald-100/50 px-8 py-4 rounded-full mb-10 shadow-lg"
            >
              <Factory className="text-[#d97925]" size={28} />
              <span className="text-[#d97925] font-semibold tracking-[0.2em] uppercase text-sm">
                Factory Compliance
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="text-6xl md:text-[100px] leading-[0.95] font-extrabold tracking-[-0.04em] mb-10"
            >
              Factory<br />
              <span className="bg-gradient-to-r from-[#d97925] via-orange-600 to-red-500 bg-clip-text text-transparent">
                Compliance
              </span><br />
              Made Bulletproof
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="text-2xl md:text-3xl text-zinc-600 max-w-4xl leading-tight"
            >
              Industrial regulations and safety standards management under the Factories Act, 1948 for manufacturing facilities.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 xl:gap-24">
            {/* Left – Text Content */}
            <div className="lg:col-span-7 space-y-20 lg:space-y-28">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9 }}
              >
                <h2 className="text-5xl font-bold tracking-tight mb-8">Overview</h2>
                <div className="space-y-8 text-[18px] leading-relaxed text-zinc-700">
                  <p>
                    The Factories Act, 1948 governs all manufacturing and industrial facilities across India, covering occupational health, safety protocols, working hours, welfare provisions, and more.
                  </p>
                  <p>
                    With increasing regulatory scrutiny and the rise of state-level amendments, factory compliance has never been more demanding.
                  </p>
                  <p className="font-medium text-zinc-800">
                    TickBox provides comprehensive factory compliance management, ensuring your facilities remain legally compliant, inspection-ready, and protected from enforcement risk.
                  </p>
                </div>
              </motion.div>

              {/* Scope of Services */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                <h3 className="text-4xl font-semibold mb-8">Scope of Services</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {scopeItems.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-emerald-50/40 p-7 rounded-2xl border border-emerald-100 flex items-start gap-4 hover:bg-emerald-50 transition-colors"
                    >
                      <ClipboardList className="text-[#d97925] mt-1 flex-shrink-0" size={24} />
                      <p className="text-lg leading-relaxed">{item}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Why This Matters */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                <h3 className="text-4xl font-semibold mb-8">Why This Matters</h3>
                <div className="bg-gradient-to-r from-red-50 to-rose-50 p-10 rounded-3xl border border-red-100 shadow-inner">
                  <div className="flex items-start gap-5">
                    <AlertTriangle className="text-red-600 mt-1.5" size={32} />
                    <div>
                      <p className="text-xl leading-relaxed text-zinc-800">
                        Factory inspections can be unannounced. Non-compliance — even a missing register or an expired licence — can lead to notices, shutdowns, and reputational damage.
                      </p>
                      <p className="mt-6 text-lg font-medium text-[#d97925]">
                        TickBox ensures you are permanently inspection-ready.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right – Visuals with parallax */}
            <motion.div
              style={{ y: imageY }}
              className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-12"
            >
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-zinc-200">
                <img
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&auto=format&fit=crop&q=80"
                  alt="Modern industrial factory floor with safety compliance"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-10 left-10 text-white">
                  <p className="text-2xl font-semibold drop-shadow-lg">Always inspection-ready</p>
                  <p className="text-base opacity-90 mt-2 drop-shadow">Managed by TickBox</p>
                </div>
              </div>

              {/* HSE Highlight Card */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-10 border border-emerald-100 shadow-xl"
              >
                <div className="flex items-center gap-5 mb-6">
                  <HardHat className="text-[#d97925]" size={48} />
                  <h4 className="text-3xl font-semibold">Health, Safety & Environment</h4>
                </div>
                <p className="text-lg text-zinc-700 leading-relaxed">
                  Full HSE compliance management — including safety protocols, welfare provisions, and hazardous process controls.
                </p>
              </motion.div>

              {/* Inspection Readiness Card */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-3xl p-10 border border-emerald-100 shadow-lg flex items-start gap-6"
              >
                <ShieldCheck className="text-[#d97925] mt-1" size={48} />
                <div>
                  <h4 className="text-3xl font-semibold mb-4">Permanent Inspection Readiness</h4>
                  <p className="text-lg text-zinc-700">
                    Mock inspections, audit preparation, liaison with inspectorates, and corrective documentation — so you're never caught off-guard.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 70 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="mt-32 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-3xl py-20 px-8 text-center shadow-2xl"
          >
            <h3 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Ready to make your factory 100% compliant?
            </h3>
            <p className="text-2xl mb-12 max-w-3xl mx-auto opacity-90">
              Book a review today and stay permanently inspection-ready with TickBox’s expert factory compliance management.
            </p>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(16, 185, 129, 0.5)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFreeConsultation}
              className="inline-flex items-center gap-4 px-16 py-8 bg-white text-[#d97925] font-semibold text-2xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-colors"
            >
              Book a Factory Compliance Review
              <ArrowRight size={32} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FactoryCompliance;